/* eslint-disable react/no-unused-state */
import React from 'react';
import { clamp, findLastIndex, get, noop } from 'lodash';
import PropTypes from 'prop-types';
import clickOutside from 'react-click-outside';

import { isStyledComponent } from 'styled-components';
import * as primitives from './primitives';
import { Breakpoints, getWindowWidth } from '../grid';
import ScrollLock from '../ScrollLock';

/**
 * A search field that shows children in a dropdown. On mobile breakpoints,
 * the dropdown expands to cover the screen.
 *
 * Pass in dropdown content as children wrapped in dropdown primitives.
 */

const dataProdGuid = 'c6790e35';

export class BaseTakeoverInput extends React.PureComponent {
    /**
     * You must pass in all the required callbacks in order
     * to capture all text box value changes.
     */
    static propTypes = {
        // for screenshots and debugging purposes
        __renderDropdown: PropTypes.bool,

        disabled: PropTypes.bool,
        defaultValue: PropTypes.string,
        onBlur: PropTypes.func,
        onChange: PropTypes.func, // input was typed in
        onClear: PropTypes.func, // clear button was clicked
        onClickOption: PropTypes.func, // option was clicked
        onClose: PropTypes.func, // dropdown is closed
        onFocus: PropTypes.func, // input was focused
        onSelect: PropTypes.func.isRequired, // option was clicked, or enter was pressed with no option focused
        placeholder: PropTypes.string,
    };

    static defaultProps = {
        defaultValue: '',
        disabled: false,
        onBlur: noop,
        onChange: noop,
        onClear: noop,
        onClickOption: noop,
        onClose: noop,
        onFocus: noop,
        placeholder: '',
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState &&
            ((nextProps.__renderDropdown && !prevState.showDropdown) ||
             (React.Children.count(nextProps.children) > 0 && prevState.focused))
        ) {
            return {
                showDropdown: true,
            };
        }

        return null;
    }

    state = {
        focused: false,
        focusedIndex: -1,
        searchQuery: this.props.defaultValue,
        showDropdown: false,
    };

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize, true);
    }

    render() {
        return (
            <primitives.Wrapper
                data-dropdown-active={String(this.state.showDropdown)}
                data-test="takeover-input"
            >
                <primitives.FieldWrapper>
                    <primitives.Field>
                        <primitives.FieldInput
                            {...this.props.inputProps}
                            autoComplete="off"
                            data-prod-guids={`takeover-input-input:[${dataProdGuid}]`}
                            data-test="takeover-input-input"
                            disabled={this.props.disabled}
                            innerRef={n => { this.$input = n; }}
                            onBlur={this.handleBlurInput}
                            onChange={this.handleChangeInput}
                            onFocus={this.handleFocusInput}
                            onKeyDown={this.handleKeyDownInput}
                            placeholder={this.props.placeholder}
                            type="text"
                            value={this.getInputDisplayValue()}
                        />
                        <primitives.ClearButton
                            data-test="takeover-input-clear-button"
                            hidden={!this.state.showDropdown || this.state.searchQuery === ''}
                            onClick={this.handleClickClear}
                        />
                    </primitives.Field>
                    <primitives.Separator hidden={!this.state.showDropdown} />
                    <primitives.CancelButton
                        data-test="takeover-input-cancel-button"
                        hidden={!this.state.showDropdown}
                        onClick={this.handleClickCancel}
                    >
                        Cancel
                    </primitives.CancelButton>
                </primitives.FieldWrapper>

                {this.maybeRenderDropdown()}
                {this.maybeRenderScrollLock()}
            </primitives.Wrapper>
        );
    }

    maybeRenderDropdown() {
        if (this.state.showDropdown) {
            return (
                <primitives.DropdownWrapper>
                    <div
                        data-prod-guids={`takeover-input-dropdown:[${dataProdGuid}]`}
                        data-test="takeover-input-dropdown"
                        onMouseLeave={this.handleMouseLeaveDropdown}
                    >
                        {React.Children.map(this.props.children, (child, index) => {
                            if (child.type === primitives.Label) { return child; }

                            const refPropName = isStyledComponent(child.type) ? 'innerRef' : 'ref';

                            return React.cloneElement(child, {
                                'data-index': index,
                                [refPropName]: n => { this[`$option_${index}`] = n; },
                                onClick: this.handleClickDropdownOption,
                                onMouseMove: this.handleMouseMoveOption,
                                'data-selected': index === this.state.focusedIndex,
                            });
                        })}
                    </div>
                </primitives.DropdownWrapper>
            );
        }
    }

    maybeRenderScrollLock() {
        if (this.state.showDropdown && getWindowWidth() < Breakpoints.small) {
            return (<ScrollLock lock />);
        }
    }

    // other methods reference this one, so it has to be declared first
    collapseDropdown = () => {
        this.setState({
            focusedIndex: -1,
            showDropdown: false,
        });
        this.props.onClose();
    };

    handleChangeInput = e => {
        this.setState({
            focusedIndex: -1,
            searchQuery: e.target.value,
        });

        this.props.onChange(e);
    };

    handleClickClear = () => {
        this.setState({ searchQuery: '' });
        this.$input.focus();
        this.props.onClear();
    };

    handleClickCancel = this.collapseDropdown;

    handleClickDropdownOption = e => {
        this.selectFocusedOption(); // assume that the option is focused when it's clicked

        if (getWindowWidth() >= Breakpoints.small) {
            this.$input.focus();
        }

        this.props.onClickOption(e);
    };

    handleClickOutside = this.collapseDropdown;

    handleBlurInput = e => {
        this.setState({ focused: false });
        this.props.onBlur(e);
    };

    handleFocusInput = e => {
        this.setState({ focused: true });
        this.maybeExpandDropdown();

        if (getWindowWidth() < Breakpoints.small) {
            this.$input.select();
        }

        this.props.onFocus(e);
    };

    handleKeyDownInput = e => {
        if (!this.state.showDropdown) { return; }

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault(); // block cursor movement
                this.moveFocus(-1);
                break;
            case 'ArrowDown':
                e.preventDefault(); // block cursor movement
                this.moveFocus(1);
                break;
            case 'Enter':
                if (this.state.focusedIndex !== -1) {
                    e.preventDefault(); // block form submission
                    this.selectFocusedOption();
                }
                break;
            case 'Escape':
                this.collapseDropdown();
                break;
            case 'Tab':
                if (this.state.focusedIndex !== -1) {
                    this.selectFocusedOption();
                } else {
                    this.collapseDropdown();
                }
                break;
        }
    };

    handleMouseLeaveDropdown = () => {
        this.setState({ focusedIndex: -1 });
    };

    handleMouseMoveOption = e => {
        const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);

        this.setState({ focusedIndex: index });
    };

    handleResize = () => {
        const width = getWindowWidth();

        if (width >= Breakpoints.small) {
            this.setState({ showDropdown: React.Children.count(this.props.children) > 0 && this.state.focused });
        } else if (width < Breakpoints.small) { // mobile
            if (document.activeElement === this.$input) {
                this.setState({ showDropdown: true });
            }
        } else if (this.state.showDropdown && !React.Children.count(this.props.children)) { // desktop
            this.collapseDropdown();
        }
    };

    getFocusedOptionValue() {
        const selectedOption = React.Children.toArray(this.props.children)[this.state.focusedIndex];

        if (selectedOption.props.value) {
            return selectedOption.props.value;
        } else if (typeof selectedOption.props.children === 'string') {
            return selectedOption.props.children;
        }

        return '';
    }

    getInputDisplayValue() {
        return this.state.focusedIndex > -1 ? this.getFocusedOptionValue() : this.state.searchQuery;
    }

    getMaxIndex() {
        return findLastIndex(React.Children.toArray(this.props.children), option => option.type !== primitives.Label);
    }

    /**
     * On mobile breakpoints, expands the dropdown.
     * On desktop breakpoint, expands the dropdown if there are dropdown items.
     */
    maybeExpandDropdown() {
        if (this.state.showDropdown) { return true; }

        if (getWindowWidth() < Breakpoints.small) { // mobile
            this.setState({ showDropdown: true });
            document.body.scrollTop = 0; // workaround for iOS where page isn't scrolled to the top
        } else if (React.Children.count(this.props.children) > 0) { // desktop
            this.setState({ showDropdown: true });
        }
    }

    /**
     * Move the dropdown focus up or down
     */
    moveFocus(delta) {
        const minIndex = -1;
        const maxIndex = this.getMaxIndex();
        let nextIndex = this.state.focusedIndex;

        do {
            nextIndex += delta;
        } while (get(this.props.children, `[${nextIndex}].type`) === primitives.Label); // skip over dropdown labels

        nextIndex = clamp(nextIndex, minIndex, maxIndex);

        // bring option into view if necessary
        if (nextIndex !== -1) {
            const optionNode = this[`$option_${nextIndex}`];
            optionNode.scrollIntoView({ block: 'nearest' });
        }

        this.setState({ focusedIndex: nextIndex });
    }

    selectFocusedOption() {
        if (this.state.focusedIndex === -1) { return; }

        const value = this.getFocusedOptionValue();
        const target = this[`$option_${this.state.focusedIndex}`];

        this.setValue(value);
        this.collapseDropdown();

        this.props.onSelect({
            target,
            value,
        });
    }

    /**
     * External API to set the text box value
     */
    setValue(value = '') {
        this.setState({ searchQuery: value });
    }
}

BaseTakeoverInput.internalProps = Object.keys(BaseTakeoverInput.defaultProps);

export default clickOutside(BaseTakeoverInput);
